import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Lock, FileText, Phone, Search, BarChart2, CheckCircle2, Loader2 } from "lucide-react";

const STEPS = [
  { icon: Globe, label: "Vérification de l'adresse du site" },
  { icon: Lock, label: "Analyse du protocole de sécurité" },
  { icon: FileText, label: "Recherche des informations légales" },
  { icon: Phone, label: "Vérification des coordonnées de contact" },
  { icon: Search, label: "Analyse du contenu et des signaux" },
  { icon: BarChart2, label: "Calcul du score de risque" },
];

export default function LoadingSteps({ domain }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (current >= STEPS.length - 1) return;
    const t = setTimeout(() => setCurrent(c => c + 1), 1800);
    return () => clearTimeout(t);
  }, [current]);

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto py-8" data-testid="loading-steps">
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="loading-dot w-2 h-2 rounded-full bg-[#163A5F]" />
          <div className="loading-dot w-2 h-2 rounded-full bg-[#163A5F]" />
          <div className="loading-dot w-2 h-2 rounded-full bg-[#163A5F]" />
        </div>
        <p className="text-base font-semibold text-slate-700">Analyse en cours</p>
        {domain && (
          <p className="text-sm text-slate-500 mt-1 font-mono">{domain}</p>
        )}
      </div>

      <div className="w-full space-y-3">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = i < current;
          const active = i === current;
          const pending = i > current;

          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: pending ? 0.35 : 1, x: 0 }}
              transition={{ delay: i * 0.1, duration: 0.3 }}
              className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                active ? "bg-[#EEF4F8] border border-[#CBD5E1]" : "bg-white border border-[#E5E7EB]"
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                done ? "bg-[#E7F3EF]" : active ? "bg-[#163A5F]/10" : "bg-[#F1F5F9]"
              }`}>
                {done ? (
                  <CheckCircle2 className="w-4 h-4 text-[#2F6B5F]" />
                ) : active ? (
                  <Loader2 className="w-4 h-4 text-[#163A5F] animate-spin" />
                ) : (
                  <Icon className="w-4 h-4 text-[#94A3B8]" />
                )}
              </div>
              <span className={`text-sm font-medium ${
                done ? "text-[#2F6B5F]" : active ? "text-[#163A5F]" : "text-[#94A3B8]"
              }`}>
                {step.label}
              </span>
            </motion.div>
          );
        })}
      </div>

      <p className="text-xs text-[#6B7280] text-center max-w-md mx-auto mt-4 leading-relaxed">
        Certaines boutiques utilisent un rendu dynamique ou limitent les vérifications automatiques. L'analyse peut prendre jusqu'à 2 minutes afin de produire un résultat plus complet.
      </p>

      <p className="mt-8 text-xs text-slate-400 text-center max-w-xs">
        Cette analyse est fournie à titre informatif. Les résultats peuvent varier selon la disponibilité du site.
      </p>
    </div>
  );
}
